import "react-image-crop/dist/ReactCrop.css";

import { Box, Button, IconButton, Slider, styled } from "@mui/material";
import { CropMaxIcon, CropMinIcon, DogIcon } from "../Icons";
import React, { FC, useRef, useState } from "react";
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import { iconActionStyle, style } from "./GvUploadImageButton.style";
import { resizeFile, uuid } from "../../utils";

import AddIcon from "@mui/icons-material/Add";
import { DialogCustom } from "../../styles/styledComponents";
import EditPhotoIcon from "../Icons/EditPhotoIcon";
import GvLoadingOverlay from "../GvLoadingOverlay/LoadingOverlay";
import ImageIcon from "../../components/Icons/ImageIcon";
import { canvasPreview } from "./canvasPreview";
import { toastMessageActions } from "../../state/request";
import { useDebounceEffect } from "./useDebounceEffect";
import { useDispatch } from "react-redux";

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

const Input = styled("input")({
  display: "none",
});

const Image = styled("img")({
  borderRadius: "50%",
  height: "100%",
  width: "100%",
  objectFit: "cover",
});

interface IUploadButtonProps {
  id: string;
  icon?: React.ReactElement;
  accept?: string;
  url?: string;
  aspect?: number;
  onChange?: (file: File) => void;
  hideUploadButton?: boolean;
}

const GvUploadImageButton: FC<IUploadButtonProps> = ({
  id,
  icon = <DogIcon width={30} height={30} />,
  accept = "image/tiff, image/jpeg, image/png, image/bmp, image/webp",
  url,
  aspect = 16 / 9,
  onChange,
  hideUploadButton=false,
}) => {
  const dispatch = useDispatch();
  const [previewFile, setSelectedFile] = useState();
  const [uncroppedImage, setUncroppedImage] = useState<string>();
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;

    setCrop(centerAspectCrop(width, height, aspect));
  };

  const onSelectFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;
    if (file.size > 10000000) {
      dispatch(toastMessageActions.setGlobalAlert({
        severity: "error",
        message: "Maximum uploaded file size allowed is 10mb",
      }));

      return;
    }


    setSelectedFile(undefined);
    setUncroppedImage(undefined);

    setIsResizing(true);
    const image = await resizeFile(file);

    setUncroppedImage(image);
    setOpenDialog(true);
    setIsResizing(false);
    event.target.value = "";
  };

  const handleCrop = () => {
    previewCanvasRef?.current?.toBlob((blob) => {
      if (!blob) {
        throw new Error("Failed to create blob");
      }
      const file = new File([blob], uuid(), {
        type: blob.type,
        lastModified: new Date().getTime(),
      });

      onChange && onChange(file);
      const reader = new FileReader();

      reader.onload = (e: any) => {
        setSelectedFile(e.target?.result);
        setOpenDialog(false);
      };
      reader.readAsDataURL(file);
    });
  };

  useDebounceEffect(async () => {
    if (
      completedCrop?.width &&
      completedCrop?.height &&
      imgRef.current &&
      previewCanvasRef.current
    ) {
      canvasPreview(
        imgRef.current,
        previewCanvasRef.current,
        completedCrop,
        scale,
      );
    }
  },
  100,
  [completedCrop, scale, uncroppedImage],
  );

  return (
    <>
      {isResizing && <GvLoadingOverlay />}

      <form encType={"multipart/form-data"}>
        <label htmlFor={id} className="cursor-pointer flex flex-col items-center">
          <Box sx={{ position: "relative" }} component="span">
            <Input
              id={id}
              accept={accept}
              multiple={false}
              type="file"
              onChange={onSelectFile}
            />
            <IconButton aria-label="upload picture" component="span" sx={style}>
              {
                (previewFile || url) ? <Image src={previewFile || url} /> : <span>{icon}</span>
              }
            </IconButton>


            <Box sx={iconActionStyle} className={hideUploadButton ? "!right-0" : ""}>
              {
                (previewFile || url) ? <EditPhotoIcon /> :
                  <span className="h-full w-full mb-1 ps-0.5 pt-0.5">
                    <AddIcon />
                  </span>

              }
            </Box>
            {!hideUploadButton && <Box className="mt-4">
              <Box className="flex items-center
                bg-taupe-50  px-4 py-2 rounded-full border border-taupe-2">
                <ImageIcon /> <span className="font-semibold ml-2 text-base tracking-wide">Upload</span>
              </Box>
            </Box>}
          </Box>

        </label>

      </form>

      <DialogCustom
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      >
        {!!uncroppedImage && (
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
          >
            <img
              ref={imgRef}
              alt="Crop me"
              src={uncroppedImage}
              style={{ transform: `scale(${scale})` }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        )}

        <Box className="flex flex-row pt-2 gap-2">
          <div>
            <CropMinIcon />
          </div>
          <Slider
            aria-label="Scale"
            value={scale}
            step={0.1}
            min={1}
            max={4}
            onChange={(e: any) => setScale(Number(e.target?.value))}
            disabled={!previewFile}
          />
          <div>
            <CropMaxIcon />
          </div>
        </Box>

        {!!completedCrop && (
          <canvas
            ref={previewCanvasRef}
            style={{
              objectFit: "contain",
              width: completedCrop.width,
              height: completedCrop.height,
              maxHeight: "100%",
              display: "none",
            }}
          />
        )}

        <Box
          className="flex flex-row pt-2 gap-2">
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            type="button"
            size="small"
            className="!capitalize"
            onClick={() => setOpenDialog(false)}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            color="primary"
            type="button"
            size="small"
            className="!capitalize"
            onClick={handleCrop}
          >
            Crop
          </Button>
        </Box>

      </DialogCustom>
    </>
  );
};

export default GvUploadImageButton;
